import { useMemo, useState } from 'react';
import {
  toWireDate,
  type DealDetail,
  type DealEvent,
  type DealEventKind,
} from '@forecastify/shared';
import { Button, Icon, Tabs, type IconName } from '@forecastify/ui';
import { useI18n, useT, type Locale } from '../../i18n';
import { formatCalendarDate, formatInstant } from '../history/history-labels';
import { dealMoney, eventKindLabel } from './deal-kit';
import { dueDateOf, type CardWrite } from './overview-parts';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D5 · the deal's activity feed.                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Newest first, as the server orders it. ⚠️ A **system** entry is not prose: the server stores a
 * machine key in `title` and its parameters as JSON in `body` (`deal.service.ts` `system`), so the
 * sentence is the catalogue's and reads in the viewer's language — not in whoever's was active
 * when the deal moved. A key this screen does not know renders a generic sentence rather than the
 * raw key.
 *
 * ⚠️ No *Fájlok* chip, which the mockup has: an upload writes no feed entry (the attachments list
 * is its own table), so the chip would filter to nothing every time.
 */

type T = ReturnType<typeof useT>;
type Filter = 'all' | 'notes' | 'todos' | 'comm' | 'system';
const FILTERS: readonly Filter[] = ['all', 'notes', 'todos', 'comm', 'system'];

function filterLabel(t: T, filter: Filter): string {
  if (filter === 'notes') return t('dealActivity.filter.notes');
  if (filter === 'todos') return t('dealActivity.filter.todos');
  if (filter === 'comm') return t('dealActivity.filter.comm');
  if (filter === 'system') return t('dealActivity.filter.system');
  return t('dealActivity.filter.all');
}

function matches(filter: Filter, kind: DealEventKind): boolean {
  if (filter === 'notes') return kind === 'note';
  if (filter === 'todos') return kind === 'task' || kind === 'meeting';
  if (filter === 'comm') return kind === 'call' || kind === 'email';
  if (filter === 'system') return kind === 'system';
  return true;
}

export function eventIcon(kind: DealEventKind): IconName {
  if (kind === 'task') return 'list-checks';
  if (kind === 'meeting') return 'calendar';
  if (kind === 'call') return 'smartphone';
  if (kind === 'email') return 'mail';
  if (kind === 'system') return 'circle-dot';
  return 'file-text';
}

/** The JSON a system entry carries, or nothing when it is not an object. */
function paramsOf(body: string): Readonly<Record<string, unknown>> {
  try {
    const parsed: unknown = JSON.parse(body);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** A wire date, or `–` for a close date that was never set. */
function day(value: unknown, locale: Locale): string {
  const raw = text(value);
  return raw === '' ? '–' : formatCalendarDate(raw, locale);
}

/** A system entry's sentence and its second line, out of the key and the parameters. */
export function systemEntry(
  t: T,
  locale: Locale,
  event: DealEvent,
  currencyCode: string,
): { readonly title: string; readonly body: string } {
  const params = paramsOf(event.body);
  switch (event.title) {
    case 'created':
      return { title: t('dealActivity.system.created'), body: '' };
    case 'status':
      return {
        title: t('dealActivity.system.status', { from: text(params.from), to: text(params.to) }),
        body: '',
      };
    case 'close_date':
      return {
        title: t('dealActivity.system.closeDate', {
          from: day(params.from, locale),
          to: day(params.to, locale),
        }),
        body: text(params.reason),
      };
    case 'won':
      return {
        title: t('dealActivity.system.won', {
          value: dealMoney(text(params.value), currencyCode),
        }),
        body: t('dealActivity.system.closedOn', { date: day(params.closedOn, locale) }),
      };
    case 'lost':
      return {
        title: t('dealActivity.system.lost'),
        body: t('dealActivity.system.closedOn', { date: day(params.closedOn, locale) }),
      };
    case 'reopened':
      return { title: t('dealActivity.system.reopened', { to: text(params.to) }), body: '' };
    /* D9 · DS-1 · a scenario was marked the winner and copied into the plan. */
    case 'scenario_won':
      return {
        title: t('dealActivity.system.scenarioWon', { name: text(params.name) }),
        body: t('dealActivity.system.scenarioWonBody'),
      };
    case 'generated': {
      const contract = text(params.contract);
      const parts = [
        params.project === true ? t('dealActivity.system.generatedProject') : '',
        params.contract === null || params.contract === undefined
          ? ''
          : contract === ''
            ? t('dealActivity.system.generatedContractPlain')
            : t('dealActivity.system.generatedContract', { contract }),
        count(params.lineItems) > 0
          ? t('dealActivity.system.generatedLineItems', { count: count(params.lineItems) })
          : '',
        count(params.tasks) > 0
          ? t('dealActivity.system.generatedTasks', { count: count(params.tasks) })
          : '',
        count(params.allocations) > 0
          ? t('dealActivity.system.generatedAllocations', { count: count(params.allocations) })
          : '',
      ].filter((part) => part !== '');
      return { title: t('dealActivity.system.generated'), body: parts.join(', ') };
    }
    default:
      return { title: t('dealActivity.system.unknown'), body: '' };
  }
}

export function ActivityFeed({
  deal,
  canManage,
  write,
}: {
  readonly deal: DealDetail;
  readonly canManage: boolean;
  readonly write: CardWrite;
}) {
  const t = useT();
  const [filter, setFilter] = useState<Filter>('all');
  const shown = useMemo(
    () => deal.events.filter((event) => matches(filter, event.kind)),
    [deal.events, filter],
  );

  return (
    <div className="flex flex-col gap-1.5">
      <Tabs
        variant="pill"
        label={t('dealActivity.filter.label')}
        value={filter}
        onValueChange={(value) => {
          setFilter(value as Filter);
        }}
        items={FILTERS.map((entry) => ({ value: entry, label: filterLabel(t, entry) }))}
      />
      {shown.length === 0 ? (
        <div className="text-muted-foreground rounded-lg border border-dashed px-3 py-3 text-[11.5px]">
          {deal.events.length === 0 ? t('dealActivity.empty') : t('dealActivity.emptyFiltered')}
        </div>
      ) : (
        <ul className="bg-card overflow-hidden rounded-lg border">
          {shown.map((event) => (
            <FeedEntry
              key={event.id}
              event={event}
              currencyCode={deal.currencyCode}
              canDelete={canManage && event.kind !== 'system'}
              onDelete={() => void write.run(`/events/${event.id}`, 'DELETE')}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function FeedEntry({
  event,
  currencyCode,
  canDelete,
  onDelete,
}: {
  readonly event: DealEvent;
  readonly currencyCode: string;
  readonly canDelete: boolean;
  readonly onDelete: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const system = event.kind === 'system' ? systemEntry(t, locale, event, currencyCode) : null;
  const title = system?.title ?? (event.title === '' ? eventKindLabel(t, event.kind) : event.title);
  const body = system?.body ?? event.body;
  const due = event.dueAt === null ? null : dueDateOf(event.dueAt);
  const late = due !== null && !event.done && due < toWireDate(new Date());

  return (
    <li className="group flex items-start gap-3 border-b px-3 py-2.5 last:border-0">
      <span className="bg-muted mt-0.5 grid size-6 shrink-0 place-items-center rounded-full">
        <Icon name={eventIcon(event.kind)} size={13} className="text-muted-foreground" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-[12.5px] font-medium">{title}</span>
          <span className="text-muted-foreground text-[11px]">
            {[eventKindLabel(t, event.kind), event.authorName ?? ''].filter(Boolean).join(' · ')}
          </span>
          <span className="text-muted-foreground text-[11px] tabular-nums">
            {formatInstant(event.occurredAt, locale)}
          </span>
          {event.done && (event.kind === 'task' || event.kind === 'meeting') ? (
            <span className="text-muted-foreground inline-flex items-center gap-1 text-[11px]">
              <Icon name="circle-check" size={11} />
              {t('dealActivity.done')}
            </span>
          ) : null}
        </div>
        {body === '' ? null : (
          <div className="mt-0.5 text-[11.5px] leading-relaxed whitespace-pre-line">{body}</div>
        )}
        {event.participants === '' ? null : (
          <div className="text-muted-foreground mt-0.5 text-[11px]">
            {t('dealActivity.participants', { names: event.participants })}
          </div>
        )}
        {due === null ? null : (
          <div
            className={`mt-0.5 text-[11px] tabular-nums ${late ? 'text-destructive' : 'text-muted-foreground'}`}
          >
            {t('dealActivity.due', { date: formatCalendarDate(due, locale) })}
          </div>
        )}
      </div>
      {canDelete ? (
        <Button
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
          aria-label={t('dealActivity.delete')}
          onClick={onDelete}
        >
          <Icon name="trash" size={13} />
        </Button>
      ) : null}
    </li>
  );
}
